import {
  Component, ViewEncapsulation, ChangeDetectionStrategy, Input, OnDestroy, Output, EventEmitter, HostListener, ChangeDetectorRef
} from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import isEmpty from 'lodash-es/isEmpty';

import { isNullOrUndefined } from '@app/util/util';
import { ANIMATIONS } from '@app/constants/animations.constants';
import { IMAGE_TYPE } from '@ux/components/image/constants/image-type.constants';
import { PositionDots, StyleDots } from '@app/modules/carousel/entities/carousel-direction';
import { DomManipulateService } from '@app/modules/core/services/dom-manipulate.service';
import { FlightCabin, FlightCabinEntertainmentType } from '@shared/flight-itinerary/entities/cabin-types';
import { HeaderWidgetType } from '@design-system/components/templates/header/entities/header.types';
import { Icon, IconFillColor } from '@design-system/components/atoms/icon/entities/icon.types';

@Component({
  selector: 'app-itinerary-cabin-detail',
  templateUrl: './itinerary-cabin-detail.component.html',
  styleUrls: ['./itinerary-cabin-detail.component.sass'],
  encapsulation: ViewEncapsulation.ShadowDom,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ItineraryCabinDetailComponent implements OnDestroy {

  @Input() cabin: FlightCabin;
  @Input() airplane: string;
  @Input() airlineName: string;

  @Output() closeCabinDetail: EventEmitter<void> = new EventEmitter<void>();

  public animation: string = ANIMATIONS.SLIDE_IN_RIGHT;
  public quantityImages: number = 5;
  public styleDots: StyleDots = StyleDots.HotelsResults;
  public typeImageFlight: string = IMAGE_TYPE.FLIGHT;
  public borderRadius: number = 8;
  public borderRadiusNone: number = 0;
  public positionDots: PositionDots = PositionDots.Right;
  public headerAnimation: boolean = false;

  private scrollTopAnimation: number = 190;

  public readonly headerWidgetType = HeaderWidgetType;

  public readonly icons = Icon;
  public readonly iconFillColor = IconFillColor;

  constructor(
    private domManipulateService: DomManipulateService,
    private translateService: TranslocoService,
    private changeDetector: ChangeDetectorRef,
  ) { }

  ngOnDestroy(): void {
    this.domManipulateService.setBodyScroll(true);
  }

  @HostListener('scroll', ['$event']) onScroll(event: Event): void {
    const offset = event.target['scrollTop'] || 0;
    this.headerAnimation = offset >= this.scrollTopAnimation;
    this.changeDetector.detectChanges();
  }

  get images(): string[] {
    if (isNullOrUndefined(this.cabin) || isNullOrUndefined(this.cabin.CabinClass)) return [];
    return this.cabin.CabinClass.Images.map(x => x.Url) || [];
  }

  get quantityImagesFlight(): number {
    if (this.images.length === 0) {
      return 1;
    }
    return this.images.length < this.quantityImages ? this.images.length : this.quantityImages;
  }

  get showDescription(): boolean {
    return !isEmpty(this.cabin.CabinClass.Description);
  }

  get showAmenities(): boolean {
    return this.cabin.Entertainment.Available || this.cabin.Power.Available || this.cabin.Wifi.Available;
  }

  get showSeats(): boolean {
    return !isEmpty(this.cabin.Seats);
  }

  get showWithCharge(): boolean {
    return (/true/i).test(this.cabin.Wifi.WithCharge);
  }

  public getImageSrc(id: number): string {
    if (this.images.length === 0) return '';
    return this.images[id];
  }

  public getEntertainmentTypes(types: FlightCabinEntertainmentType[]): string {
    return types.map(x =>
      this.translateService.translate(`FLIGHT_ITEM_DETAIL_MODULE.CABIN.ENTERTAINMENT.TYPES.${x.toUpperCase()}`)
    ).join(', ');
  }

  public close(): void {
    this.closeCabinDetail.emit();
  }

}
